import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ConfigProvider, Image, Input, Modal, Select, Skeleton } from "antd";
import { DownloadOutlined, QrcodeOutlined } from "@ant-design/icons";
import { PDFDownloadLink } from "@react-pdf/renderer";
import dayjs from "dayjs";
import useAxiosClient from "../axios-client.js";
import { useStateContext } from "../contexts/ContextProvider";
import OrderInvoice from "../components/OrderInvoice/OrderInvoice";
import OrderQrLabel from "../components/OrderInvoice/OrderQrLabel";
import OrderQrCode from "../components/OrderQrCode";
import { orderDeepLink } from "../utils/orderQr";
import { extractOrderNumber } from "../components/Util/OrderNumberFormatter";
import {
  OrderStatusEnum,
  canChangeAnyStatus,
} from "../utils/enums/OrderStatusEnum";
import StatusChangeModal from "../components/Order/StatusChangeModal";
import { fmtBDT, statusMeta } from "../components/Order/orderPrimitives";
import { PRIMARY, SHELL_THEME as THEME, shellAntdTheme, STATUS } from "../shellTokens";

// The production span a stepper walks, in flow order (ADR 0006). HOLD and the
// exits are deliberately off it — they are departures from the line, not steps
// along it.
const MAIN_FLOW = [1, 2, 3, 5, 9, 10, 6];
const HOLD = 11;
const DELIVERED = 6;
const OUT_FOR_DELIVERY = 10;
const API = process.env.REACT_APP_API_BASE_URL;
// Only an order that has not been produced yet can be filled from ready stock
// (ADR 0003); BOOKING left this list when it moved to after READY.
const ELIGIBLE_STATUSES = [1, 2];

const Order = () => {
  const axiosClient = useAxiosClient();
  const { permissions, darkMode, company } = useStateContext();
  const canPull = permissions?.includes("PULL_FROM_STOCK");
  const canReturn = permissions?.includes("RETURN_ORDER");
  const canChangeStatus = canChangeAnyStatus(permissions);

  const [order, setOrder] = useState({});
  const [activeTab, setActiveTab] = useState("items");
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnSelections, setReturnSelections] = useState({});
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [presetStatus, setPresetStatus] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [returnComment, setReturnComment] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();

  const fetchOrders = useCallback(async () => {
    try {
      const res = await axiosClient.get(
        `/orders/getOrder/${extractOrderNumber(id)}`
      );
      setOrder({ ...res.data.data });
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      navigate("/notFound");
    }
  }, [axiosClient, id, navigate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handlePull = async (item) => {
    try {
      const res = await axiosClient.post(
        `/orders/pullFromStock/${order.id}/${item.id}`
      );
      toast.success(res.data.message);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const submitReturns = async () => {
    const lines = Object.entries(returnSelections)
      .filter(([, c]) => c)
      .map(([pid, c]) => ({ productId: Number(pid), condition: c }));
    if (!lines.length) {
      toast.warning(
        "Set at least one product to Sellable or Damaged before recording the return."
      );
      return;
    }
    try {
      const res = await axiosClient.post(`/orders/markReturned/${order.id}`, {
        lines,
        comment: returnComment.trim(),
      });
      toast.success(res.data.message);
      setReturnOpen(false);
      setReturnSelections({});
      setReturnComment("");
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  /** `preset` lets Release open the modal with the held-from status chosen. */
  const openStatusModal = (preset = null) => {
    if (!canChangeStatus) return;
    setPresetStatus(preset);
    setStatusModalOpen(true);
  };

  // One endpoint for every transition (ADR 0006): the reason, the hold category
  // and the per-line restock decisions all travel with it, so a cancel from here
  // is the same transaction as one from the Orders list.
  const confirmStatusChange = async (payload) => {
    setStatusSubmitting(true);
    try {
      const res = await axiosClient.post("/orders/updateOrderStatus", {
        orderId: order.id,
        ...payload,
      });
      toast.success(res.data.message || "Order status updated");
      setStatusModalOpen(false);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setStatusSubmitting(false);
    }
  };

  // Modals portal to <body>, outside the element rootStyle publishes these on,
  // so anything inside one needs the hex rather than var(--x).
  const shell = THEME[darkMode ? "dark" : "light"];

  const rootStyle = {
    ...Object.fromEntries(
      Object.entries(THEME[darkMode ? "dark" : "light"]).map(([k, v]) => [
        `--${k}`,
        v,
      ])
    ),
    "--primary": PRIMARY,
  };

  if (!order.id) {
    return (
      <div style={{ ...rootStyle, padding: 24 }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  const statusInfo = statusMeta(order.status);
  // A held order's way back: the status it was held from, offered as one click.
  const releaseTo = order.status === HOLD ? order.heldFromStatus : null;
  const isMerchant = !order.customer;
  const products = order.products || [];
  const timeline = [...(order.orderStatusChanges || [])].reverse();

  // ---- Status stepper ----
  const historyStatuses = (order.orderStatusChanges || []).map((t) => t.status);
  const visited = new Set(historyStatuses);
  visited.add(order.status);
  visited.add(1);
  if (historyStatuses.length === 0) {
    // Position in the flow, not the id: the ids are not in flow order (BOOKING
    // is 9, DELIVERED is 6), so comparing them lit up steps the order never
    // reached. An off-flow status (hold, returned, cancelled) has none to fill.
    const at = MAIN_FLOW.indexOf(order.status);
    MAIN_FLOW.slice(0, at + 1).forEach((val) => visited.add(val));
  }
  const steps = MAIN_FLOW.map((val, i) => {
    const done = visited.has(val) && val !== order.status;
    const current = val === order.status;
    const info = OrderStatusEnum.find((s) => s.value === val);
    return {
      label: info.label,
      mark: done ? "✓" : String(i + 1),
      circleBg: done ? STATUS.ok : current ? info.color : "var(--borderStrong)",
      active: done || current,
      hasLine: i < MAIN_FLOW.length - 1,
      lineDone: visited.has(val) && visited.has(MAIN_FLOW[i + 1]),
    };
  });

  return (
    <div className="et-order-detail" style={rootStyle}>
      <style>{`
        .et-order-detail{font-family:Outfit,system-ui,sans-serif;color:var(--text);height:100%;min-height:0;display:flex;flex-direction:column}
        .et-order-detail ::-webkit-scrollbar{width:7px}
        .et-order-detail ::-webkit-scrollbar-thumb{background:var(--borderStrong);border-radius:5px}
        .et-order-detail ::-webkit-scrollbar-track{background:transparent}
        .od-tab:hover{color:var(--text)}
        .od-btn-ghost:hover{background:var(--hover)}
        .od-btn-ghost-danger:hover{background:rgba(248,113,113,.12);color:${STATUS.error}}
        .od-pull-btn:hover{background:var(--activeBg)}
      `}</style>

      {/* ---- Main card ---- */}
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 14,
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* ---- Header ---- */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: 14,
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span
                style={{
                  font: "700 20px Outfit, sans-serif",
                  color: "var(--text)",
                }}
              >
                Order #{order.id}
              </span>
              <span
                onClick={openStatusModal}
                style={{
                  font: "600 11px Outfit, sans-serif",
                  color: "#fff",
                  padding: "3px 10px",
                  borderRadius: 10,
                  background: statusInfo.color,
                  cursor: canChangeStatus ? "pointer" : "default",
                }}
              >
                {statusInfo.label}
              </span>
            </div>
            <div style={{ font: "400 13px Outfit, sans-serif", color: "var(--muted)" }}>
              {order.customer?.name ||
                order.marketplace?.name ||
                order.orderable?.name ||
                "—"}{" "}
              · {dayjs(order.createdAt).format("MMM D, YYYY")}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignSelf: "flex-start", flexWrap: "wrap" }}>
            {/* Release from hold — the held-from status, one click away */}
            {canChangeStatus && releaseTo && (
              <button
                type="button"
                onClick={() => openStatusModal(releaseTo)}
                title={`Release to ${statusMeta(releaseTo).label}`}
                style={{ ...iconBtn, width: "auto", padding: "0 12px" }}
              >
                Release to {statusMeta(releaseTo).label}
              </button>
            )}
            {/* Change Status */}
            {canChangeStatus && (
              <button
                type="button"
                onClick={() => openStatusModal()}
                title="Change status"
                style={iconBtn}
              >
                ⇄
              </button>
            )}
            {/* Edit Order */}
            <button
              type="button"
              onClick={() => navigate(`/orders/${order.id}/edit`)}
              title="Edit order"
              style={iconBtn}
            >
              ✎
            </button>
            <PDFDownloadLink
              document={<OrderInvoice order={order} company={company} />}
              fileName={`order-${order.id}-${dayjs().unix()}.pdf`}
            >
              {({ loading }) => (
                <button
                  type="button"
                  disabled={loading}
                  style={{
                    height: 36,
                    padding: "0 14px",
                    borderRadius: 9,
                    border: "1.5px dashed var(--primary)",
                    background: "transparent",
                    color: "var(--primary)",
                    font: "700 12px Outfit, sans-serif",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                  }}
                >
                  <DownloadOutlined /> Download PDF
                </button>
              )}
            </PDFDownloadLink>
            <PDFDownloadLink
              document={<OrderQrLabel order={order} />}
              fileName={`order-${order.id}-label.pdf`}
            >
              {({ loading }) => (
                <button
                  type="button"
                  disabled={loading}
                  style={{
                    height: 36,
                    padding: "0 14px",
                    borderRadius: 9,
                    border: "1.5px dashed var(--border)",
                    background: "transparent",
                    color: "var(--muted)",
                    font: "700 12px Outfit, sans-serif",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                  }}
                >
                  <QrcodeOutlined /> QR label
                </button>
              )}
            </PDFDownloadLink>
          </div>
        </div>

        {/* ---- Two-column body ---- */}
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "stretch",
            flex: 1,
            minHeight: 0,
          }}
          className="max-lg:flex-col"
        >
          {/* ---- Left: stepper + tabbed content ---- */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {/* Status stepper */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                overflowX: "auto",
                paddingBottom: 8,
                flexShrink: 0,
              }}
            >
              {steps.map((st, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      minWidth: 64,
                    }}
                  >
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: st.circleBg,
                        color: st.done ? "#fff" : "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        font: "700 10px Outfit, sans-serif",
                      }}
                    >
                      {st.mark}
                    </div>
                    <div
                      style={{
                        font: "600 10.5px Outfit, sans-serif",
                        whiteSpace: "nowrap",
                        color: st.active ? "var(--text)" : "var(--dim)",
                      }}
                    >
                      {st.label}
                    </div>
                  </div>
                  {st.hasLine && (
                    <div
                      style={{
                        width: 34,
                        height: 2,
                        marginBottom: 16,
                        flexShrink: 0,
                        background: st.lineDone
                          ? STATUS.ok
                          : "var(--border)",
                      }}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div
              style={{
                display: "flex",
                gap: 2,
                background: "var(--surface2)",
                borderRadius: 10,
                padding: 3,
                width: "fit-content",
                marginBottom: 12,
                flexShrink: 0,
              }}
            >
              {[
                { k: "items", l: "Items" },
                { k: "timeline", l: "History" },
              ].map((t) => (
                <div
                  key={t.k}
                  className="od-tab"
                  onClick={() => setActiveTab(t.k)}
                  style={{
                    padding: "7px 16px",
                    borderRadius: 8,
                    font: "600 12.5px Outfit, sans-serif",
                    cursor: "pointer",
                    background:
                      activeTab === t.k ? "var(--surface)" : "transparent",
                    color:
                      activeTab === t.k
                        ? "var(--text)"
                        : "var(--muted)",
                    boxShadow:
                      activeTab === t.k
                        ? "0 1px 3px rgba(0,0,0,.12)"
                        : "none",
                    transition: "background .15s",
                  }}
                >
                  {t.l}
                </div>
              ))}
            </div>

            {/* Tab content */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                paddingRight: 4,
              }}
            >
              {activeTab === "items" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {/* Goods can come back once they are with the courier, not
                      only after delivery — a refused parcel is a return too. */}
                  {canReturn && [DELIVERED, OUT_FOR_DELIVERY].includes(order.status) && (
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button
                        type="button"
                        className="od-btn-ghost-danger"
                        onClick={() => setReturnOpen(true)}
                        style={{
                          height: 30,
                          padding: "0 12px",
                          borderRadius: 7,
                          border: "1px solid rgba(248,113,113,.4)",
                          background: "transparent",
                          color: STATUS.error,
                          font: "600 12px Outfit, sans-serif",
                          cursor: "pointer",
                        }}
                      >
                        Mark returned
                      </button>
                    </div>
                  )}

                  {products.map((p) => (
                    <div
                      key={p.id}
                      style={{
                        display: "flex",
                        gap: 12,
                        padding: 12,
                        borderRadius: 12,
                        border: "1px solid var(--border)",
                      }}
                    >
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 10,
                          overflow: "hidden",
                          flexShrink: 0,
                          background: "var(--surface2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--muted)",
                          font: "700 16px Outfit, sans-serif",
                        }}
                      >
                        {p.productType?.name?.[0] || "?"}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <div
                            style={{
                              font: "600 13.5px Outfit, sans-serif",
                              color: "var(--text)",
                            }}
                          >
                            {p.productType?.name}
                          </div>
                          <div
                            style={{
                              font: "600 13.5px Outfit, sans-serif",
                              color: "var(--text)",
                            }}
                          >
                            {fmtBDT(p.price)}
                          </div>
                        </div>
                        <div
                          style={{
                            font: "600 11.5px Outfit, sans-serif",
                            color: "var(--primary)",
                            marginTop: 1,
                          }}
                        >
                          {[p.fabrics?.name, p.fabricType?.name, p.productFormation?.name]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                        <div
                          style={{
                            font: "400 12px Outfit, sans-serif",
                            color: "var(--muted)",
                            marginTop: 1,
                          }}
                        >
                          {p.description || "No description"} · Qty {p.unit}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginTop: 6,
                            flexWrap: "wrap",
                          }}
                        >
                          {p.fulfilledFromStock && (
                            <span style={tagStyle(STATUS.ok)}>
                              Fulfilled from stock
                            </span>
                          )}
                          {p.isReturned && (
                            <span
                              style={tagStyle(
                                p.returnCondition === "SELLABLE"
                                  ? "#f59e0b"
                                  : STATUS.error
                              )}
                            >
                              Returned ({p.returnCondition})
                            </span>
                          )}
                          {!p.fulfilledFromStock &&
                            ELIGIBLE_STATUSES.includes(order.status) &&
                            p.availableInStock > 0 && (
                              <>
                                <span style={tagStyle(STATUS.ok)}>
                                  {p.availableInStock} in ready stock
                                </span>
                                {canPull && (
                                  <button
                                    type="button"
                                    className="od-pull-btn"
                                    onClick={() => handlePull(p)}
                                    style={{
                                      height: 26,
                                      padding: "0 10px",
                                      borderRadius: 7,
                                      border: "none",
                                      background: "transparent",
                                      color: "var(--primary)",
                                      font: "600 12px Outfit, sans-serif",
                                      cursor: "pointer",
                                    }}
                                  >
                                    Pull from stock
                                  </button>
                                )}
                              </>
                            )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Attachments */}
                  <div style={{ marginTop: 8 }}>
                    <div
                      style={{
                        font: "600 11px Outfit, sans-serif",
                        color: "var(--dim)",
                        textTransform: "uppercase",
                        letterSpacing: ".05em",
                        marginBottom: 6,
                      }}
                    >
                      Attachments
                    </div>
                    {order.images && order.images.length ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                        {order.images.map((img) => (
                          <Image
                            key={img.id}
                            width={132}
                            height={132}
                            src={`${API}/files/upload/${img.id}`}
                            style={{
                              objectFit: "cover",
                              borderRadius: 10,
                              border: "1px solid var(--border)",
                            }}
                            preview={{ mask: "View" }}
                          />
                        ))}
                      </div>
                    ) : (
                      <div
                        style={{
                          font: "400 12.5px Outfit, sans-serif",
                          color: "var(--dim)",
                        }}
                      >
                        No attachments.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "timeline" && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {timeline.length ? (
                    timeline.map((t, i) => {
                      const info = OrderStatusEnum.find(
                        (s) => s.value === t.status
                      ) || {};
                      return (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            gap: 10,
                            padding: "10px 0",
                            borderBottom: "1px solid var(--border)",
                          }}
                        >
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              marginTop: 5,
                              flexShrink: 0,
                              background: info.color || "var(--dim)",
                            }}
                          />
                          <div>
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <span
                                style={{
                                  font: "600 11px Outfit, sans-serif",
                                  color: "#fff",
                                  padding: "2px 8px",
                                  borderRadius: 8,
                                  background: info.color || "var(--dim)",
                                }}
                              >
                                {info.label}
                              </span>
                              <span
                                style={{
                                  font: "400 11px Outfit, sans-serif",
                                  color: "var(--dim)",
                                }}
                              >
                                {dayjs(t.created_at).format(
                                  "MMM D, YYYY h:mm A"
                                )}
                              </span>
                            </div>
                            {t.comment && (
                              <div
                                style={{
                                  font: "400 12.5px Outfit, sans-serif",
                                  color: "var(--text)",
                                  marginTop: 4,
                                }}
                              >
                                {t.comment}
                              </div>
                            )}
                            {t.user && (
                              <div
                                style={{
                                  font: "400 11px Outfit, sans-serif",
                                  color: "var(--dim)",
                                  fontStyle: "italic",
                                  marginTop: 2,
                                }}
                              >
                                by {t.user.firstname}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        font: "400 12.5px Outfit, sans-serif",
                        color: "var(--dim)",
                        padding: "16px 0",
                      }}
                    >
                      No history yet.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ---- Right: summary panel ---- */}
          <div
            style={{
              width: 280,
              flexShrink: 0,
              background: "var(--surface2)",
              borderRadius: 14,
              padding: 14,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              overflowY: "auto",
            }}
            className="max-lg:w-full"
          >
            {/* Customer / Merchant */}
            <div>
              <div style={panelLabel}>
                {isMerchant ? "Merchant" : "Customer"}
              </div>
              {isMerchant ? (
                <>
                  <div style={panelValue}>
                    {order.orderable?.name}
                  </div>
                  {order.merchantRef && (
                    <div
                      style={{
                        font: "400 12px Outfit, sans-serif",
                        color: "var(--muted)",
                        marginTop: 2,
                      }}
                    >
                      Ref: {order.merchantRef}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={panelValue}>
                    {order.customer?.name}
                  </div>
                  <div
                    style={{
                      font: "400 12px Outfit, sans-serif",
                      color: "var(--muted)",
                      marginTop: 2,
                    }}
                  >
                    {[
                      order.customer?.address?.address,
                      order.customer?.address?.upazila?.name,
                      order.customer?.address?.district?.name,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                  <div
                    style={{
                      font: "400 12px Outfit, sans-serif",
                      color: "var(--muted)",
                    }}
                  >
                    {[order.customer?.address?.phone, order.customer?.altPhone]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  {order.customer?.facebook && (
                    <a
                      href={
                        /^https?:\/\//.test(order.customer.facebook)
                          ? order.customer.facebook
                          : `https://facebook.com/${order.customer.facebook}`
                      }
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "block",
                        font: "400 12px Outfit, sans-serif",
                        color: "var(--primary)",
                        marginTop: 2,
                        wordBreak: "break-all",
                      }}
                    >
                      {order.customer.facebook}
                    </a>
                  )}
                </>
              )}
            </div>

            <div style={{ height: 1, background: "var(--border)" }} />

            {/* Delivery */}
            <div>
              <div style={panelLabel}>Delivery</div>
              <div style={panelRow}>
                <span>Channel</span>
                <span style={{ fontWeight: 600 }}>
                  {order.deliveryChannel?.name || "—"}
                </span>
              </div>
              <div style={{ ...panelRow, marginTop: 4 }}>
                <span>Date</span>
                <span style={{ fontWeight: 600 }}>
                  {dayjs(order.deliveryDate).format("MMM D, YYYY")}
                </span>
              </div>
            </div>

            <div style={{ height: 1, background: "var(--border)" }} />

            {/* Payment */}
            <div>
              <div style={panelLabel}>Payment</div>
              <div style={panelRow}>
                <span>Subtotal</span>
                <span>{fmtBDT(order.payment?.amount)}</span>
              </div>
              <div style={{ ...panelRow, marginTop: 4 }}>
                <span>Delivery</span>
                <span>{fmtBDT(order.payment?.deliveryCharge)}</span>
              </div>
              <div
                style={{
                  height: 1,
                  background: "var(--border)",
                  margin: "6px 0",
                }}
              />
              <div style={{ ...panelRow, fontWeight: 700, fontSize: 15 }}>
                <span>Total</span>
                <span>{fmtBDT(order.payment?.totalAmount)}</span>
              </div>
            </div>

            <div style={{ height: 1, background: "var(--border)" }} />

            {/* QR */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ ...panelLabel, alignSelf: "flex-start" }}>
                Order QR
              </div>
              <div
                style={{
                  padding: 8,
                  background: "var(--surface)",
                  borderRadius: 12,
                  border: "1px solid var(--border)",
                }}
              >
                <OrderQrCode value={orderDeepLink(order.id)} size={104} />
              </div>
              <div
                style={{
                  font: "400 10.5px Outfit, sans-serif",
                  color: "var(--dim)",
                  marginTop: 6,
                  textAlign: "center",
                }}
              >
                Scan to open · advances status
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Responsive helper ---- */}
      <style>{`
        @media (max-width: 1024px) {
          .max-lg\\:flex-col { flex-direction: column !important; }
          .max-lg\\:w-full { width: 100% !important; }
        }
      `}</style>

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        {/* ---- Status modal (shared with the Orders list) ---- */}
        <StatusChangeModal
          open={statusModalOpen}
          order={order}
          permissions={permissions}
          initialStatus={presetStatus}
          submitting={statusSubmitting}
          onCancel={() => setStatusModalOpen(false)}
          onSubmit={confirmStatusChange}
        />

        {/* ---- Returns modal ---- */}
        <Modal
          title="Mark lines as returned"
          open={returnOpen}
          onOk={submitReturns}
          onCancel={() => setReturnOpen(false)}
          okText="Record return"
          okButtonProps={{ danger: true, disabled: !returnComment.trim() }}
        >
          <div
            style={{
              font: "400 12.5px Outfit, sans-serif",
              color: shell.muted,
            }}
          >
            Set each returned line to <b>Sellable</b> or <b>Damaged</b> (leave
            others as "Not returned"). Sellable lines are added to ready stock.
          </div>
          {/* A return is an exit: it needs a reason, same as a cancellation. */}
          <Input.TextArea
            rows={2}
            style={{ marginTop: 12 }}
            placeholder="Why is it coming back? (required)"
            value={returnComment}
            onChange={(e) => setReturnComment(e.target.value)}
            maxLength={200}
            showCount
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginTop: 12,
            }}
          >
            {products.length === 0 && (
              <div
                style={{
                  font: "400 12.5px Outfit, sans-serif",
                  color: shell.dim,
                }}
              >
                This order has no products.
              </div>
            )}
            {products.map((p) => (
              <div
                key={p.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <span style={{ font: "400 12.5px Outfit, sans-serif" }}>
                  {p.productType?.name} · {p.fabrics?.name} · Qty {p.unit}
                </span>
                {p.isReturned ? (
                  <span
                    style={tagStyle(
                      p.returnCondition === "SELLABLE" ? "#f59e0b" : STATUS.error
                    )}
                  >
                    Returned ({p.returnCondition})
                  </span>
                ) : (
                  <Select
                    allowClear
                    style={{ width: 150 }}
                    placeholder="Not returned"
                    value={returnSelections[p.id]}
                    onChange={(v) =>
                      setReturnSelections((prev) => ({
                        ...prev,
                        [p.id]: v,
                      }))
                    }
                    options={[
                      { value: "SELLABLE", label: "Sellable" },
                      { value: "DAMAGED", label: "Damaged" },
                    ]}
                  />
                )}
              </div>
            ))}
          </div>
        </Modal>
      </ConfigProvider>
    </div>
  );
};

/* ---- Shared styles ---- */
const panelLabel = {
  font: "600 11px Outfit, sans-serif",
  color: "var(--dim)",
  textTransform: "uppercase",
  letterSpacing: ".05em",
  marginBottom: 4,
};

const panelValue = {
  font: "600 13.5px Outfit, sans-serif",
  color: "var(--text)",
};

const panelRow = {
  display: "flex",
  justifyContent: "space-between",
  font: "400 12.5px Outfit, sans-serif",
  color: "var(--text)",
};

const iconBtn = {
  width: 36,
  height: 36,
  borderRadius: 9,
  border: "1px solid var(--border)",
  background: "var(--surface2)",
  color: "var(--muted)",
  fontSize: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  padding: 0,
};

const tagStyle = (color) => ({
  display: "inline-flex",
  font: "600 10.5px Outfit, sans-serif",
  color,
  background: `${color}22`,
  padding: "2px 8px",
  borderRadius: 6,
});

export default Order;
